import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  AlertTriangle,
  Check,
  CheckCircle2,
  List,
  RotateCw,
  Target,
} from 'lucide-react';
import { PageHeader } from 'src/components/common/PageHeader';
import { PageContainer } from 'src/components/common/PageContainer';
import MetricTile from 'src/components/common/MetricTile';
import AppSelect from 'src/components/common/AppSelect';
import SearchInput from 'src/components/common/SearchInput';
import TablePagination from 'src/components/common/TablePagination';
import { Button } from 'src/components/ui/button';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from 'src/components/ui/table';
import { cn, formatDate } from 'src/lib/utils';
import { useToast } from 'src/hooks/use-toast';
import {
  useSkillResolutionQueue,
  useSkillResolutionQueueDetail,
} from 'src/services/api/queries/skillResolutionQueries';
import {
  useResolveNewSkill,
  useResolveAlias,
  useResolveMap,
  useResolveBlock,
} from 'src/services/api/mutations/skillResolutionMutations';
import { useSkillsList } from 'src/services/api/queries/referenceDataQueries';
import RecordDrawer from './RecordDrawer';
import SkillActionDialog from './SkillActionDialog';
import { SuggestedActionPill } from './SuggestedActionPill';
import {
  BORDERLINE,
  HEALTH,
  HEALTH_MIX_COLORS,
  STATUS_META,
  suggestAction,
  findRefSkill,
  adaptQueueItem,
  mergeQueueDetail,
} from './skillResolutionData';

const TABS = [
  { key: 'queue', label: 'Queue' },
  { key: 'borderline', label: 'Borderline audit' },
  { key: 'health', label: 'Health' },
];

const STATUS_OPTIONS = [
  { label: 'Open', value: 'open' },
  { label: 'Ignored', value: 'ignored' },
  { label: 'Blocked', value: 'blocked' },
  { label: 'All', value: 'all' },
];
// Not rendered right now (see the filter bar below) — kept for when the
// side filter comes back.
// eslint-disable-next-line no-unused-vars
const SIDE_OPTIONS = [
  { label: 'All sides', value: 'all' },
  { label: 'Candidate', value: 'candidate' },
  { label: 'Requirement', value: 'requirement' },
];
const MIN_OPTIONS = [
  { label: '1+ occurrences', value: '1' },
  { label: '3+ occurrences', value: '3' },
  { label: '5+ occurrences', value: '5' },
  { label: '10+ occurrences', value: '10' },
];
const SORT_OPTIONS = [
  { label: 'Sort: Occurrences', value: 'occurrences' },
  { label: 'Sort: Mandatory first', value: 'priority' },
  { label: 'Sort: Last seen', value: 'last_seen' },
];
// Filters to rows whose `suggested_action` badge matches — lets you sanity
// check the backend's own suggestions against reality (e.g. `map` should
// only ever be rows with an exact_hit). 'all' is a UI-only sentinel; the
// param itself is omitted from the request rather than sent as "all" (see
// queueQuery below), matching the API's documented convention.
const SUGGESTED_OPTIONS = [
  { label: 'All suggestions', value: 'all' },
  { label: 'Map', value: 'map' },
  { label: 'Alias', value: 'alias' },
  { label: 'New skill', value: 'new_skill' },
  { label: 'Ignore', value: 'ignore' },
];

const DEFAULT_PAGE_SIZE = 8;

// Actions with a real resolve endpoint — see handleActionSubmit, which
// calls the matching mutation directly instead of computeLocalActionResult.
const WIRED_ACTIONS = new Set(['NEW_SKILL', 'ALIAS', 'MAP', 'BLOCK']);

// Ignore/Unignore/Unmap still have no backend endpoint, so their effect is
// applied locally as an "override" layered on top of the live queue row
// (see `overrides` state below) rather than persisted server-side.
function computeLocalActionResult(item, action) {
  const entry = {
    action,
    actor: 'you',
    at: new Date().toISOString(),
    payload: {},
    result: {},
  };
  const history = [...item.history, entry];

  switch (action) {
    case 'IGNORE':
      return {
        patch: { status: 'ignored', history },
        message: `"${item.norm}" hidden from the queue. It keeps counting occurrences.`,
        variant: 'success',
      };
    case 'UNIGNORE':
      return {
        patch: { status: 'open', history },
        message: `"${item.norm}" is back in the open queue.`,
        variant: 'success',
      };
    case 'UNMAP':
      return {
        patch: { status: 'open', healedTo: null, history },
        message: `${item.cand + item.req} rows reversed to NONE. The alias is still active — deactivate it if the mapping was wrong.`,
        variant: 'warning',
      };
    default:
      return { patch: {}, message: '', variant: 'default' };
  }
}

function NearestCell({ item }) {
  if (item.exact) {
    return (
      <div>
        <div className="text-[12.5px] font-semibold text-foreground-strong">
          {item.exact.name}
        </div>
        <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-sm text-[10.5px] font-semibold bg-ds-success-subtle text-ds-success">
          <Check className="w-[10px] h-[10px]" strokeWidth={2.6} />
          Exact match
        </span>
      </div>
    );
  }
  if (!item.nearest.length) {
    return (
      <div>
        <span className="text-muted-foreground text-[13px]">—</span>
        <div className="text-[11px] text-muted-foreground">
          no parse-time score
        </div>
      </div>
    );
  }
  const [id, name, cnt, score] = item.nearest[0];
  let barClass = 'bg-ds-warning';
  if (score >= 0.7) barClass = 'bg-ds-success';
  else if (score >= 0.55) barClass = 'bg-primary';
  return (
    <div className="min-w-[170px]">
      <div className="text-[12.5px] font-semibold text-foreground-strong">
        {name}{' '}
        <span className="text-[11px] font-normal text-muted-foreground">
          · id {id}
        </span>
      </div>
      <div className="flex items-center gap-2 mt-1">
        <span className="w-[70px] h-[5px] rounded-full bg-muted overflow-hidden shrink-0">
          <span
            className={cn('block h-full rounded-full', barClass)}
            style={{ width: `${Math.round(score * 100)}%` }}
          />
        </span>
        <span className="font-mono text-[11px] font-semibold text-foreground-strong">
          {score.toFixed(3)}
        </span>
        <span className="text-[11px] text-muted-foreground">· {cnt} rows</span>
      </div>
    </div>
  );
}
NearestCell.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  item: PropTypes.object.isRequired,
};

const HealthMixCard = ({ title, rows }) => (
  <div className="bg-card border border-border rounded-xl">
    <div className="px-4 py-3 border-b border-border-light">
      <h3 className="text-[13px] font-bold text-foreground-strong">{title}</h3>
    </div>
    <div className="px-4 py-4">
      <div className="flex h-2.5 rounded-full overflow-hidden bg-muted">
        {rows.map(([label, pct]) => (
          <span
            key={label}
            style={{
              width: `${(pct * 100).toFixed(1)}%`,
              backgroundColor: HEALTH_MIX_COLORS[label],
            }}
          />
        ))}
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1.5 mt-3">
        {rows.map(([label, pct]) => (
          <span
            key={label}
            className="inline-flex items-center gap-1.5 text-[11.5px] text-muted-foreground"
          >
            <span
              className="w-2 h-2 rounded-sm shrink-0"
              style={{ backgroundColor: HEALTH_MIX_COLORS[label] }}
            />
            {label}{' '}
            <span className="font-mono font-semibold text-foreground-strong">
              {(pct * 100).toFixed(1)}%
            </span>
          </span>
        ))}
      </div>
    </div>
  </div>
);
HealthMixCard.propTypes = {
  title: PropTypes.string.isRequired,
  rows: PropTypes.arrayOf(
    PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))
  ).isRequired,
};

const SkillResolution = () => {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('queue');
  // Locally-applied action results, keyed by name_norm. For the four wired
  // actions this is an immediate optimistic patch layered on top of the
  // real mutation (see handleActionSubmit) so the row updates instantly
  // instead of waiting on a refetch; for ignore/unignore/unmap it's still
  // the only record of the action, since those have no endpoint yet.
  const [overrides, setOverrides] = useState({});

  // The real reference-skill catalog (id ↔ name), used to populate the
  // Alias/Map/Unmap target picker. `sessionSkills` layers on any skill
  // created via NEW_SKILL this session that the (hour-long cached)
  // reference-data query doesn't know about yet.
  const { skills: masterSkills } = useSkillsList();
  const [sessionSkills, setSessionSkills] = useState([]);
  const refSkills = useMemo(() => {
    const masterIds = new Set(masterSkills.map((s) => s.id));
    return [
      ...masterSkills,
      ...sessionSkills.filter((s) => !masterIds.has(s.id)),
    ];
  }, [masterSkills, sessionSkills]);

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('open');
  const [sideFilter, setSideFilter] = useState('all');
  const [minOcc, setMinOcc] = useState('3');
  const [sortBy, setSortBy] = useState('occurrences');
  const [suggestedFilter, setSuggestedFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  // Page resets to 1 in the same tick as the filter change itself (not a
  // separate effect reacting to it) — otherwise React can commit a render
  // with the new filter and the still-stale page number, which fires a
  // request for a page/filter combination that never really existed.
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  const withPageReset = (setter) => (value) => {
    setter(value);
    setPage(1);
  };

  const [seenNorms, setSeenNorms] = useState(() => new Set());

  const [drawerNorms, setDrawerNorms] = useState([]);
  const [drawerIndex, setDrawerIndex] = useState(0);
  const [autoAdvance, setAutoAdvance] = useState(true);
  const [actionState, setActionState] = useState({
    action: null,
    presetTargetId: null,
  });

  const queueQuery = useSkillResolutionQueue({
    status: statusFilter,
    side: sideFilter,
    minOccurrences: minOcc,
    sort: sortBy,
    suggested: suggestedFilter === 'all' ? undefined : suggestedFilter,
    search: debouncedSearch || undefined,
    page,
    pageSize,
  });
  // This endpoint returns the payload flat — no `{success, data}` envelope
  // like most of the app's other list endpoints.
  const rawItems = queueQuery.data?.items ?? [];
  const items = useMemo(
    () => rawItems.map((raw) => adaptQueueItem(raw, overrides[raw.name_norm])),
    [rawItems, overrides]
  );
  const pagination = queueQuery.data?.pagination;
  const totalPages = pagination?.total_pages ?? 1;
  const totalItems = pagination?.total ?? 0;
  const currentPage = pagination?.page ?? page;

  // Fixed, independent of the active filters — drives the "Open norms" tile
  // and the Queue tab badge, so they don't jump around as you filter the
  // table itself.
  const openCountQuery = useSkillResolutionQueue({
    status: 'open',
    side: 'all',
    minOccurrences: '1',
    sort: 'occurrences',
    search: undefined,
    page: 1,
    pageSize: 1,
  });
  const openCount = openCountQuery.data?.pagination?.total ?? 0;

  const drawerOpen = drawerNorms.length > 0 && drawerIndex < drawerNorms.length;
  const drawerItemLive = drawerOpen
    ? items.find((it) => it.norm === drawerNorms[drawerIndex]) || null
    : null;

  // A wired action's invalidation can make the just-acted-on row fall out
  // of the current filtered/paginated results (e.g. Map on a status=open
  // view) before the user closes the modal. Rather than let it go blank
  // mid-review, fall back to the last version of it we actually saw.
  const lastKnownItemsRef = useRef({});
  useEffect(() => {
    if (drawerItemLive) {
      lastKnownItemsRef.current[drawerItemLive.norm] = drawerItemLive;
    }
  }, [drawerItemLive]);
  const drawerItem =
    drawerItemLive ??
    (drawerOpen ? lastKnownItemsRef.current[drawerNorms[drawerIndex]] : null) ??
    null;

  // Record detail — real variant counts, nearest-match distribution,
  // affected rows, a collision preview, and history for whichever norm is
  // open. Layered on top of the list-shaped `drawerItem` (see
  // mergeQueueDetail) so the modal already has something sensible to show
  // while this is loading.
  const detailQuery = useSkillResolutionQueueDetail(drawerItem?.norm, {
    enabled: drawerOpen,
  });
  const enrichedDrawerItem = useMemo(
    () => mergeQueueDetail(drawerItem, detailQuery.data),
    [drawerItem, detailQuery.data]
  );

  const deadZoneCandidatePct =
    (HEALTH.dead.candidate.unresolved / HEALTH.dead.candidate.total) * 100;
  const deadZoneRequirementPct =
    (HEALTH.dead.requirement.unresolved / HEALTH.dead.requirement.total) * 100;
  const healedByCurators = useMemo(() => {
    const sessionHealed = Object.values(overrides).reduce(
      (sum, o) =>
        sum +
        (o.history || []).reduce(
          (s, h) =>
            s +
            (h.result?.healed
              ? h.result.healed.candidate + h.result.healed.requirement
              : 0),
          0
        ),
      0
    );
    return (
      HEALTH.backfilled.candidate +
      HEALTH.backfilled.requirement +
      sessionHealed
    );
  }, [overrides]);

  const resetFilters = () => {
    setSearch('');
    setDebouncedSearch('');
    setStatusFilter('open');
    setSideFilter('all');
    setMinOcc('3');
    setSortBy('occurrences');
    setSuggestedFilter('all');
    setPage(1);
  };

  const openRecord = (norm) => {
    const list = items.length ? items.map((it) => it.norm) : [norm];
    const idx = list.indexOf(norm);
    setDrawerNorms(list);
    setDrawerIndex(idx < 0 ? 0 : idx);
    setSeenNorms((prev) => new Set(prev).add(norm));
  };
  const closeDrawer = () => setDrawerNorms([]);
  const gotoPrev = () => setDrawerIndex((i) => Math.max(0, i - 1));
  const gotoNext = () =>
    setDrawerIndex((i) => {
      const next = Math.min(drawerNorms.length - 1, i + 1);
      setSeenNorms((prev) => new Set(prev).add(drawerNorms[next]));
      return next;
    });

  const openAction = (action, presetTargetId) =>
    setActionState({ action, presetTargetId: presetTargetId ?? null });
  const closeAction = () =>
    setActionState({ action: null, presetTargetId: null });

  const resolveNewSkill = useResolveNewSkill();
  const resolveAlias = useResolveAlias();
  const resolveMap = useResolveMap();
  const resolveBlock = useResolveBlock();
  const wiredMutations = {
    NEW_SKILL: resolveNewSkill,
    ALIAS: resolveAlias,
    MAP: resolveMap,
    BLOCK: resolveBlock,
  };

  // Called by SkillActionDialog's submit button. For the four wired actions
  // this awaits the real mutation — a rejection propagates back up so the
  // dialog can show the server's error inline instead of closing. Only on
  // confirmed success do we patch the row, close, toast, and advance.
  const handleActionSubmit = async (action, body) => {
    if (!enrichedDrawerItem) return;
    const item = enrichedDrawerItem;

    if (WIRED_ACTIONS.has(action)) {
      const response = await wiredMutations[action].mutateAsync(body);
      const result = response?.result || {};

      const target = body.ref_skill_id
        ? findRefSkill(refSkills, body.ref_skill_id)
        : null;

      const entry = {
        action,
        actor: 'you',
        at: new Date().toISOString(),
        payload: body,
        result,
      };
      const patch = { history: [...item.history, entry] };
      if (action === 'BLOCK') {
        patch.status = 'blocked';
      } else {
        patch.status = 'resolved';
        patch.healedTo =
          result.ref_skill_id ??
          result.created?.skill_id ??
          body.ref_skill_id ??
          null;
      }
      setOverrides((prev) => ({
        ...prev,
        [item.norm]: { ...prev[item.norm], ...patch },
      }));

      // The server now knows the real reference-skill id it created —
      // reflect it locally so it's selectable in this session's Alias/Map
      // target dropdowns without a dedicated reference-skill-list endpoint.
      if (action === 'NEW_SKILL' && result.created?.skill_id) {
        setSessionSkills((prev) => [
          ...prev,
          { id: result.created.skill_id, name: body.name },
        ]);
      }

      const healedTotal = result.healed?.total;
      const skippedTotal = result.skipped?.total;
      const healedNote =
        healedTotal != null
          ? ` — ${healedTotal} row${healedTotal === 1 ? '' : 's'} healed${skippedTotal ? `, ${skippedTotal} skipped` : ''}.`
          : '.';
      const messages = {
        NEW_SKILL: `"${body.name}" created${healedNote}`,
        ALIAS: `Alias added to ${target?.name || 'the skill'}${healedNote}`,
        MAP: `Linked to ${target?.name || 'the skill'}${healedNote}`,
        BLOCK: `"${item.norm}" added to the blocklist.`,
      };
      const advisoryNote = result.advisory ? ` ${result.advisory}` : '';
      closeAction();
      toast({
        message: `${messages[action]}${advisoryNote}`,
        variant: result.advisory ? 'warning' : 'success',
        duration: 3200,
      });
      if (autoAdvance) setDrawerIndex((i) => i + 1);
      return;
    }

    const { patch, message, variant } = computeLocalActionResult(item, action);
    setOverrides((prev) => ({
      ...prev,
      [item.norm]: { ...prev[item.norm], ...patch },
    }));
    closeAction();
    toast({ message, variant, duration: 3200 });
    if (autoAdvance) {
      // Advance to the next norm in the reviewed list; if this was the last
      // one, drawerIndex runs past the end and the drawer closes itself.
      setDrawerIndex((i) => i + 1);
    }
  };

  const handleBorderlineBlock = (displayName) => {
    toast({
      message: `Blocking "${displayName}" stops future parses. The existing link stays until a taxonomy fix.`,
      variant: 'warning',
      duration: 3200,
    });
  };

  return (
    <div>
      <PageHeader title="Skill Resolution" />
      <PageContainer>
        <header className="mb-[22px]">
          <h1 className="text-[22px] font-bold tracking-[-0.04em] text-foreground-strong mb-1">
            Skill Resolution
          </h1>
          <p className="text-[13.5px] text-muted-foreground leading-[1.55] max-w-[78ch]">
            Every skill the parser couldn&apos;t map to the taxonomy, grouped by
            normalised name. Decide what each one is — a new skill, an alias, a
            one-off, or noise — and history is healed the moment you do.
          </p>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 mt-2.5 text-[11.5px] text-muted-foreground">
            <span>
              Taxonomy{' '}
              <span className="font-mono font-semibold text-foreground-strong">
                {refSkills.length} reference skills
              </span>
            </span>
            <span className="text-muted-foreground/50">·</span>
            <span>
              Actions never auto-apply — every taxonomy change is yours.
            </span>
          </div>
        </header>

        {/* Summary metric strip */}
        <div className="grid grid-cols-5 gap-[14px] mb-[18px]">
          <MetricTile
            icon={List}
            iconBg="hsl(var(--brand-bg-subtle))"
            iconColor="hsl(var(--primary))"
            value={openCount}
            label="Open norms"
          />
          <MetricTile
            icon={AlertTriangle}
            iconBg="var(--ds-warning-subtle)"
            iconColor="var(--ds-warning)"
            value={`${deadZoneCandidatePct.toFixed(1)}%`}
            label="Dead zone — candidate"
          />
          <MetricTile
            icon={AlertTriangle}
            iconBg="var(--ds-warning-subtle)"
            iconColor="var(--ds-warning)"
            value={`${deadZoneRequirementPct.toFixed(1)}%`}
            label="Dead zone — requirement"
          />
          <MetricTile
            icon={CheckCircle2}
            iconBg="var(--ds-success-subtle)"
            iconColor="var(--ds-success)"
            value={healedByCurators.toLocaleString()}
            label="Healed by curators"
          />
          <MetricTile
            icon={Target}
            iconBg="#F3F0FF"
            iconColor="#7C3AED"
            value={BORDERLINE.length}
            label="Borderline accepted"
          />
        </div>

        {/* Tab bar */}
        <div className="flex items-center border-b border-border mb-[18px]">
          {TABS.map(({ key, label }) => {
            const isActive = activeTab === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => setActiveTab(key)}
                className={cn(
                  'px-[18px] py-[10px] text-[13px] border-b-2 -mb-px transition-colors',
                  isActive
                    ? 'font-semibold border-primary text-primary'
                    : 'font-medium border-transparent text-muted-foreground hover:text-foreground-strong'
                )}
              >
                {label}
                {key === 'queue' && (
                  <span
                    className={cn(
                      'ml-2 inline-flex items-center px-[7px] py-px rounded-full text-[11px] font-semibold font-mono',
                      isActive
                        ? 'bg-brand-bg-subtle text-primary'
                        : 'bg-border text-muted-foreground'
                    )}
                  >
                    {openCount}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {activeTab === 'queue' && (
          <div className="bg-card border border-border rounded-xl overflow-hidden">
            {/* Filter bar */}
            <div className="flex flex-wrap items-center gap-2.5 px-4 py-3 border-b border-border-light">
              <SearchInput
                value={search}
                onValueChange={setSearch}
                placeholder="Search norms — e.g. tally, analysis"
                className="w-[210px]"
              />
              <AppSelect
                options={STATUS_OPTIONS}
                value={statusFilter}
                onValueChange={withPageReset(setStatusFilter)}
                className="h-9 w-[115px] text-[12.5px]"
              />
              {/* Hidden for now — sideFilter still defaults to 'all' and is
                  still sent on every request, just not user-selectable. */}
              <AppSelect
                options={MIN_OPTIONS}
                value={minOcc}
                onValueChange={withPageReset(setMinOcc)}
                className="h-9 w-[145px] text-[12.5px]"
              />
              <AppSelect
                options={SUGGESTED_OPTIONS}
                value={suggestedFilter}
                onValueChange={withPageReset(setSuggestedFilter)}
                className="h-9 w-[160px] text-[12.5px]"
              />
              <AppSelect
                options={SORT_OPTIONS}
                value={sortBy}
                onValueChange={withPageReset(setSortBy)}
                className="h-9 w-[180px] text-[12.5px]"
              />
              {queueQuery.isFetching && !queueQuery.isLoading && (
                <RotateCw
                  className="w-3.5 h-3.5 text-muted-foreground animate-spin shrink-0 ml-auto"
                  aria-label="Refreshing"
                />
              )}
            </div>

            {debouncedSearch && (
              <div className="px-4 pt-3">
                <div className="flex items-center gap-2 px-3.5 py-2.5 rounded-lg bg-brand-bg-subtle text-[12.5px] text-foreground-strong">
                  <span>
                    <strong className="font-semibold">{totalItems}</strong> norm
                    {totalItems === 1 ? '' : 's'} matching &quot;
                    {debouncedSearch}&quot;.
                  </span>
                  <button
                    type="button"
                    className="ml-auto text-primary font-semibold underline underline-offset-2"
                    onClick={() => {
                      setSearch('');
                      setDebouncedSearch('');
                      setPage(1);
                    }}
                  >
                    Clear search
                  </button>
                </div>
              </div>
            )}

            {queueQuery.isError && (
              <div className="px-4 pt-3">
                <div className="flex items-center gap-2 px-3.5 py-2.5 rounded-lg bg-ds-danger-subtle text-ds-danger">
                  <span>Couldn&apos;t load the queue. Please try again.</span>
                  <button
                    type="button"
                    className="ml-auto font-semibold underline underline-offset-2"
                    onClick={() => queueQuery.refetch()}
                  >
                    Retry
                  </button>
                </div>
              </div>
            )}

            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Normalised name</TableHead>
                    <TableHead className="text-right">Occurrences</TableHead>
                    <TableHead>Nearest reference skill</TableHead>
                    <TableHead>Last seen</TableHead>
                    <TableHead>Suggested</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>
                      <span className="sr-only">Actions</span>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {queueQuery.isLoading && (
                    <TableRow>
                      <TableCell
                        colSpan={7}
                        className="text-center py-10 text-muted-foreground text-[13px]"
                      >
                        Loading queue…
                      </TableCell>
                    </TableRow>
                  )}
                  {!queueQuery.isLoading &&
                    !queueQuery.isError &&
                    items.length === 0 && (
                      <TableRow>
                        <TableCell
                          colSpan={7}
                          className="text-center py-10 text-muted-foreground text-[13px]"
                        >
                          Nothing matches these filters.{' '}
                          <button
                            type="button"
                            className="text-primary font-semibold underline underline-offset-2"
                            onClick={resetFilters}
                          >
                            Reset filters
                          </button>
                        </TableCell>
                      </TableRow>
                    )}
                  {items.map((it) => {
                    const sg = suggestAction(it);
                    const st = STATUS_META[it.status];
                    return (
                      <TableRow
                        key={it.norm}
                        className="cursor-pointer"
                        onClick={() => openRecord(it.norm)}
                      >
                        <TableCell className="min-w-[220px]">
                          <div className="flex items-center gap-1.5 text-[13px] font-semibold text-foreground-strong">
                            {seenNorms.has(it.norm) && (
                              <span
                                className="w-[6px] h-[6px] rounded-full bg-primary shrink-0"
                                title="Reviewed"
                              />
                            )}
                            {it.norm}
                            {it.mandatory && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] font-bold bg-ds-warning-subtle text-ds-warning">
                                Mandatory req
                              </span>
                            )}
                          </div>
                          <div className="text-[11.5px] text-muted-foreground mt-0.5">
                            {it.variants.map((v) => v[0]).join(' · ')}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="font-mono text-[13px] font-semibold text-foreground-strong">
                            {it.cand + it.req}
                          </div>
                          <div className="text-[11px] text-muted-foreground">
                            {it.cand} cand · {it.req} req
                          </div>
                        </TableCell>
                        <TableCell>
                          <NearestCell item={it} />
                        </TableCell>
                        <TableCell className="text-[12.5px] text-muted-foreground whitespace-nowrap">
                          {formatDate(it.lastSeen)}
                        </TableCell>
                        <TableCell>
                          {sg ? (
                            <SuggestedActionPill action={sg} />
                          ) : (
                            <span className="text-muted-foreground text-[13px]">
                              —
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          <span className="inline-flex items-center gap-1.5 text-[12.5px] text-muted-foreground whitespace-nowrap">
                            <span
                              className={cn(
                                'w-[7px] h-[7px] rounded-full shrink-0',
                                st.dotClass
                              )}
                            />
                            {st.label}
                          </span>
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              openRecord(it.norm);
                            }}
                          >
                            Review
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            {totalItems > 0 && (
              <div className="px-4 pb-4">
                <TablePagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  pageSize={pageSize}
                  totalItems={totalItems}
                  onPageChange={setPage}
                  onPageSizeChange={(size) => {
                    setPageSize(size);
                    setPage(1);
                  }}
                  pageSizeOptions={[8, 20, 50]}
                  itemLabel="norms"
                />
              </div>
            )}
          </div>
        )}

        {activeTab === 'borderline' && (
          <div>
            <div className="flex items-start gap-2.5 px-4 py-3 rounded-lg bg-brand-bg-subtle text-[12.5px] text-foreground-strong mb-4">
              <span>
                <strong className="font-semibold">Read-only audit.</strong>{' '}
                These links were made by the parser at 0.78–0.82 (candidate) /
                0.80–0.84 (requirement) — accepted, but close to the floor.{' '}
                <span className="text-muted-foreground">
                  A wrong one is evidence for a taxonomy fix or a blocklist
                  entry; unlinking a parser-made row isn&apos;t available yet.
                </span>
              </span>
            </div>
            <div className="bg-card border border-border rounded-xl overflow-hidden">
              <div className="px-4 py-3 border-b border-border-light">
                <h2 className="text-[14px] font-bold text-foreground-strong">
                  Borderline accepted links
                </h2>
                <p className="text-[11.5px] text-muted-foreground mt-0.5">
                  Lowest score first — the most likely false positives are at
                  the top
                </p>
              </div>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Side</TableHead>
                      <TableHead>Parsed name</TableHead>
                      <TableHead>Linked to</TableHead>
                      <TableHead className="text-right">Score</TableHead>
                      <TableHead>Row</TableHead>
                      <TableHead>
                        <span className="sr-only">Actions</span>
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {BORDERLINE.map((b) => (
                      <TableRow key={b.row} className="hover:bg-transparent">
                        <TableCell>
                          <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-semibold bg-muted text-muted-foreground capitalize">
                            {b.side}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div className="text-[13px] font-semibold text-foreground-strong">
                            {b.name}
                          </div>
                          <div className="text-[11px] text-muted-foreground font-mono">
                            {b.norm}
                          </div>
                        </TableCell>
                        <TableCell className="text-[12.5px] text-foreground-strong">
                          {b.to[1]}{' '}
                          <span className="text-muted-foreground">
                            · id {b.to[0]}
                          </span>
                        </TableCell>
                        <TableCell className="text-right font-mono text-[12.5px] font-semibold text-foreground-strong">
                          {b.score.toFixed(3)}
                        </TableCell>
                        <TableCell className="font-mono text-[11.5px] text-muted-foreground">
                          {b.row}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleBorderlineBlock(b.name)}
                          >
                            Block term
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'health' && (
          <div className="space-y-[14px]">
            <div className="grid grid-cols-2 gap-[14px]">
              <HealthMixCard
                title="Method mix — candidate skills"
                rows={HEALTH.mix.candidate}
              />
              <HealthMixCard
                title="Method mix — requirement skills"
                rows={HEALTH.mix.requirement}
              />
            </div>
            <div className="grid grid-cols-2 gap-[14px]">
              <div className="bg-card border border-border rounded-xl">
                <div className="px-4 py-3 border-b border-border-light">
                  <h3 className="text-[13px] font-bold text-foreground-strong">
                    Dead zone
                  </h3>
                  <p className="text-[11.5px] text-muted-foreground mt-0.5">
                    Unresolved rows, by side
                  </p>
                </div>
                <div className="px-4 py-4 space-y-4">
                  {[
                    ['candidate', HEALTH.dead.candidate],
                    ['requirement', HEALTH.dead.requirement],
                  ].map(([side, d]) => {
                    const pct = (d.unresolved / d.total) * 100;
                    return (
                      <div key={side}>
                        <div className="flex items-center gap-2.5">
                          <span className="w-[110px] text-[12px] text-muted-foreground capitalize shrink-0">
                            {side}
                          </span>
                          <span className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden">
                            <span
                              className="block h-full rounded-full bg-ds-warning"
                              style={{
                                width: `${Math.min(100, pct * 6).toFixed(1)}%`,
                              }}
                            />
                          </span>
                          <span className="font-mono text-[12px] font-semibold text-foreground-strong w-[48px] text-right">
                            {pct.toFixed(1)}%
                          </span>
                        </div>
                        <div className="text-[11px] text-muted-foreground mt-1 ml-[122px]">
                          {d.unresolved.toLocaleString()} unresolved of{' '}
                          {d.total.toLocaleString()} rows
                        </div>
                      </div>
                    );
                  })}
                  <p className="text-[11px] text-muted-foreground pt-1 border-t border-border-light">
                    Bars are scaled ×6 so single-digit percentages stay
                    readable; the printed number is the real value.
                  </p>
                </div>
              </div>

              <div className="bg-card border border-border rounded-xl">
                <div className="px-4 py-3 border-b border-border-light">
                  <h3 className="text-[13px] font-bold text-foreground-strong">
                    Queue &amp; backfill totals
                  </h3>
                </div>
                <div className="px-4 py-4">
                  <dl className="grid grid-cols-[1fr_auto] gap-y-2 text-[13px]">
                    <dt className="text-muted-foreground">Norms in queue</dt>
                    <dd className="text-foreground-strong font-semibold text-right">
                      {openCount}
                    </dd>
                    <dt className="text-muted-foreground">
                      Backfilled — candidate
                    </dt>
                    <dd className="text-foreground-strong font-semibold text-right">
                      {HEALTH.backfilled.candidate.toLocaleString()}
                    </dd>
                    <dt className="text-muted-foreground">
                      Backfilled — requirement
                    </dt>
                    <dd className="text-foreground-strong font-semibold text-right">
                      {HEALTH.backfilled.requirement.toLocaleString()}
                    </dd>
                  </dl>
                  <div className="flex items-start gap-2.5 px-3.5 py-2.5 rounded-lg bg-ds-success-subtle text-ds-success text-[12.5px] mt-4">
                    <CheckCircle2
                      className="w-4 h-4 shrink-0 mt-0.5"
                      strokeWidth={2}
                    />
                    <span className="text-foreground-strong">
                      EXACT share is up 4.1 points over 12 weeks and the
                      candidate dead zone is down from 9.7% — the aliases you
                      added are doing the work.
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </PageContainer>

      <RecordDrawer
        open={drawerOpen}
        onClose={closeDrawer}
        item={enrichedDrawerItem}
        detailLoading={detailQuery.isLoading}
        position={drawerIndex + 1}
        total={drawerNorms.length}
        refSkills={refSkills}
        autoAdvance={autoAdvance}
        onAutoAdvanceChange={setAutoAdvance}
        actionOpen={!!actionState.action}
        onPrev={gotoPrev}
        onNext={gotoNext}
        onAction={openAction}
      />
      <SkillActionDialog
        open={!!actionState.action}
        onClose={closeAction}
        action={actionState.action}
        item={enrichedDrawerItem}
        presetTargetId={actionState.presetTargetId}
        refSkills={refSkills}
        onSubmit={handleActionSubmit}
      />
    </div>
  );
};

export default SkillResolution;
